import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlayCircle } from 'lucide-react';
import { formatDuration, videoFiles, type Video } from '@/content/videos';

/** Phones start with the 480p copy; anyone can switch. */
const startsSmall = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;

/**
 * A tutorial video with English captions on, an HD / data-saver switch that
 * keeps your place, the AI-voice note, and the transcript (tap a line to jump
 * there; left out when `compact`, as on the landing page).
 */
export function VideoPlayer({ video, compact = false }: { video: Video; compact?: boolean }) {
  const files = videoFiles(video.slug);
  const ref = useRef<HTMLVideoElement>(null);
  const resume = useRef<{ time: number; play: boolean } | null>(null);
  const [small, setSmall] = useState(startsSmall);

  const switchQuality = () => {
    const v = ref.current;
    if (v) resume.current = { time: v.currentTime, play: !v.paused };
    setSmall((s) => !s);
  };
  const seek = (time: number) => {
    const v = ref.current;
    if (!v) return;
    v.currentTime = time;
    void v.play().catch(() => undefined);
  };

  return (
    <div className="space-y-4">
      <video
        ref={ref}
        key={small ? 'sd' : 'hd'}
        src={small ? files.sd : files.hd}
        poster={files.poster}
        controls
        playsInline
        preload="metadata"
        className="aspect-video w-full rounded-xl border border-border bg-black"
        onLoadedMetadata={(e) => {
          const r = resume.current;
          if (!r) return;
          resume.current = null;
          e.currentTarget.currentTime = r.time;
          if (r.play) void e.currentTarget.play().catch(() => undefined);
        }}
      >
        <track kind="captions" src={files.captions} srcLang="en" label="English" default />
      </video>
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
        <p>Narrated with an AI voice · {formatDuration(video.duration)}</p>
        <button
          type="button"
          onClick={switchQuality}
          className="-my-2 min-h-10 rounded-lg px-2 font-medium text-primary hover:bg-muted"
        >
          {small ? 'Switch to HD' : 'Use less data (480p)'}
        </button>
      </div>
      {!compact && (
        <details className="rounded-xl border border-border bg-card p-4">
          <summary className="-m-4 cursor-pointer p-4 font-semibold">Transcript</summary>
          <ol className="mt-2 space-y-1 text-sm">
            {video.transcript.map((t) => (
              <li key={t.start}>
                <button
                  type="button"
                  onClick={() => seek(t.start)}
                  className="-mx-2 flex w-full gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-muted"
                >
                  <span className="w-10 shrink-0 text-muted-foreground tabular-nums">
                    {formatDuration(t.start)}
                  </span>
                  <span>{t.text}</span>
                </button>
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}

/** A video in a list: poster, length, title and summary. */
export function VideoCard({ video }: { video: Video }) {
  const files = videoFiles(video.slug);
  return (
    <Link
      to={`/videos/${video.slug}`}
      className="group block h-full overflow-hidden rounded-xl border border-border bg-card hover:border-primary"
    >
      <span className="relative block aspect-video bg-black">
        <img src={files.poster} alt="" loading="lazy" className="size-full object-cover" />
        <span className="absolute inset-0 flex items-center justify-center opacity-90 group-hover:opacity-100">
          <PlayCircle className="size-12 text-white drop-shadow" aria-hidden />
        </span>
        <span className="absolute right-2 bottom-2 rounded bg-black/70 px-1.5 py-0.5 text-xs font-medium text-white tabular-nums">
          {formatDuration(video.duration)}
        </span>
      </span>
      <span className="block space-y-1 p-4">
        <span className="block font-semibold">{video.title}</span>
        <span className="block text-sm text-muted-foreground">{video.summary}</span>
      </span>
    </Link>
  );
}

/** A compact "Watch" link for the top of a guide chapter. */
export function WatchLink({ video }: { video: Video }) {
  const files = videoFiles(video.slug);
  return (
    <Link
      to={`/videos/${video.slug}`}
      className="flex items-center gap-3 rounded-xl border border-border bg-card p-2 pr-4 hover:border-primary"
    >
      <span className="relative block aspect-video w-28 shrink-0 overflow-hidden rounded-lg bg-black">
        <img src={files.poster} alt="" loading="lazy" className="size-full object-cover" />
        <PlayCircle className="absolute inset-0 m-auto size-7 text-white drop-shadow" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block text-xs font-semibold tracking-wider text-primary uppercase">
          Watch · {formatDuration(video.duration)}
        </span>
        <span className="block font-semibold">{video.title}</span>
      </span>
    </Link>
  );
}
